import { useState, useEffect } from "react";
import PropTypes from 'prop-types';

// @mui material components
import { Grid, Tabs, Tab, Box, Card } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

//Backend access
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useMatch, useParams, useLocation, Link } from 'react-router-dom';

import { useAuth0 } from "@auth0/auth0-react";

import bksAPI from "services/api/bks";
import groupAPI from "services/api/groups";
import sitesAPI from "services/api/sites";
import gtfsAPI from "services/api/gtfs";

import { setupDeviceTabSelectors } from "utils/setup";
import { wrapper, loadingScreen } from "utils/wrapper";
import { processDeviceData } from "utils/device";
import { useSoftUIController } from "hooks/context";

import DeviceOverviewTab from "pages/components/Devices/DeviceView/Overview";
import CameraFeedTab from "pages/components/Devices/DeviceView/CameraFeed";
import EventsTab from "pages/components/Devices/DeviceView/Events";
import StatisticsTab from "pages/components/Devices/DeviceView/Statistics";
import GenerateReportTab from "pages/components/Devices/DeviceView/GenerateReport";
import DeviceSettingsTab from "pages/components/Devices/DeviceView/Settings";
import { disableMap } from "utils/map";
import { deviceViewWrapper } from "utils/wrapper";
import SoftButton from "components/SoftComponents/SoftButton";
import LiveView from "components/LiveView";
import DeviceInspectionHistoryTab from "pages/components/Devices/DeviceView/History";
import { processGroupData } from "utils/group";

import LiveviewTimeline from "components/Timelines/LiveviewTimeline";
import OccupancyFeed from "components/Camera/OccupancyFeed";
import AllCampaignsView from "./AllCampaignsView";

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 1, py: 0, height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function DeviceView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    bksAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());

    const navigate = useNavigate();
    const { uuid } = useParams();

    const [tabValue, setTabValue] = useState(0);
    const [isPerspectiveLiveview, setIsPerspectiveLiveview] = useState(true);
    const [directionTrigger, setDirectionTrigger] = useState({ prev: 0, next: 0 });

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    var group_data = null;
    var device_data = null;

    const handleTabClick = (event, newValue) => {
        setTabValue(newValue);
    };

    const device = useQuery({
        queryKey: ['getDevice', uuid, selectedOrg],
        queryFn: async () => {
            return await bksAPI.getDevice(uuid);
        },
        refetchInterval: 20 * 1000
    });

    const device_groups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const tags = useQuery({
        queryKey: ['getAllTags', selectedOrg],
        queryFn: async () => {
            return await gtfsAPI.getTags();
        }
    });


    const group = useQuery({
        queryKey: ['getGroupWithDeviceUUID', uuid, selectedOrg],
        enabled: !!device.data && !!(device.data?.organisation_id == "clearways"),
        queryFn: async () => {
            return await groupAPI.getGroupWithDeviceUUID(uuid);
        }
    });

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Prevent arrows from scrolling the page
            if (e.keyCode === 37 || e.keyCode === 39) {
                if (e.keyCode === 39) { // Right
                    setDirectionTrigger(prev => ({ ...prev, next: prev.next + 1 }));
                } else if (e.keyCode === 37) { // Left
                    setDirectionTrigger(prev => ({ ...prev, prev: prev.prev + 1 }));
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, []);

    useEffect(() => {
        const deviceMissing = !device.isLoading && !device.data;
        const groupMissingForClearways = !device.isLoading && device.data?.organisation_id === "clearways" && !group.isLoading && !group.data;

        if (deviceMissing || (deviceMissing && groupMissingForClearways)) {
            navigate('/devices', { replace: true });
        }

    }, [device.data, device.isLoading, group.data, group.isLoading, navigate]);

    useEffect(() => {
        if (!device_data) return;

        var active_tabs = setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout);
        var indexes = active_tabs.map(tab => tab.index).reverse();
        if (indexes.indexOf(tabValue) > -1) {
            setTabValue(tabValue);
        }
        else {
            setTabValue(indexes.find(num => num < tabValue));
        }
    }, [deviceLayout, device_data]);

    const isGroupLoading = device.data?.organisation_id === "clearways" && group.isLoading;

    if (device.isLoading || isGroupLoading) {
        return loadingScreen(controller, true);
    }

    if (device.error || group.error) {
        return wrapper("An error has occurred fetching data", controller);
    }

    group_data = group.data && device.data.organisation_id == "clearways" ? processGroupData(group.dataUpdatedAt, group.data, [device.data], showAdvancedDeviceLevel) : false;
    device_data = device.data && processDeviceData(device.dataUpdatedAt, device.data);

    // GROUPS ADDITION
    var initialTags = [
        ...(device_groups.data || []).filter(dg => dg.type == "campaigns" && dg.sites.indexOf(device?.data?.site_id) > -1).map(dg => ({
            category: 'Group', field: 'group_id', value: dg.group_id, id: dg.group_id, label: dg.name
        })),
        ...(tags.data?.stops || [{ category: 'Site', field: 'stop_id', value: device?.data?.site_id, id: device?.data?.site_id, label: `${device?.data?.site_id}` }]).filter(r => r?.site_id == device?.data?.site_id).map((r) => ({
            category: r?.creation_type?.indexOf('gtfs') > 0 ? 'Stop' : 'Site', field: 'stop_id', value: r.site_id, id: r.site_id, label: (r?.site_name ? `${r.stop_id} - ${r.site_name}` : `${r.stop_id}`)
        }))
    ];

    const tabGeneration = () => {
        var tabs = setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout);
        return tabs.map(function (tab) {
            return <Tab key={tab.index} sx={{ minWidth: "fit-content !important", padding: '12px !important' }} value={tab.index} label={tab.label} {...a11yProps(tab.index)} />
        });
    }

    if (!device.isLoading && device.data) {
        const liveviewInfo = (
            <>
                {(device_data.hardware_type == 'bk-camera' && device_data.organisation_id != "clearways") ? (
                    <Box sx={{ height: deviceLayout != "mobile" && "calc(100svh - 10.25rem - 16px)" }}>
                        <OccupancyFeed device={device_data} user={user} deviceLayout={deviceLayout} selectedOrg={selectedOrg} report={true} sx={{ height: "100%" }} />
                    </Box>
                ) : (
                    <>
                        {(device_data?.config?.liveview_type == "history") ? (
                            <LiveviewTimeline uuid={uuid} device={device_data} deviceLayout={deviceLayout} selectedOrg={selectedOrg} isPerspectiveLiveview={device_data?.overlay_id && device_data?.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} setIsPerspectiveLiveview={setIsPerspectiveLiveview} directionTrigger={directionTrigger} />
                        ) : (
                            <Box display="flex" flex={1} flexDirection={{ xxs: "column" }} height="calc(100svh - 10.25rem - 16px)"> {/* Need to fix for mobile state */}
                                {(device_data?.overlay_id && device_data.overlay_id != 'no_frame') ? (
                                    <>
                                        <Box flex={{ xxs: 1 }} pb={1}>
                                            <SoftBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                                {(device_data?.overlay_id && device_data?.overlay_id != 'no_frame') && (
                                                    <SoftButton variant="contained" color="dark" circular={true} onClick={() => { setIsPerspectiveLiveview(!isPerspectiveLiveview) }}
                                                        sx={{ fontSize: '13px', mx: 1, minHeight: '32px !important' }}>
                                                        Switch View
                                                    </SoftButton>
                                                )}
                                            </SoftBox>
                                        </Box>
                                        <Box display="flex" flex={{ xxs: 11 }} sx={{ maxHeight: 'calc(100svh - 16rem - 16px)' }}>
                                            <LiveView device={device_data} nexus_id={user.organisations.find((org) => org.organisation_id == device_data.organisation_id).nexus_id || null} selectedOrg={selectedOrg} isPerspectiveLiveview={device_data?.overlay_id && device_data?.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                                        </Box>
                                    </>
                                ) : (
                                    <Box display="flex" alignItems="center" flex={{ xxs: 12 }} sx={{ maxHeight: 'calc(100svh - 10.25rem - 16px)' }}>
                                        <LiveView device={device_data} nexus_id={user.organisations.find((org) => org.organisation_id == device_data.organisation_id).nexus_id || null} selectedOrg={selectedOrg} isPerspectiveLiveview={device_data?.overlay_id && device_data.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </>
        );

        return wrapper((
            <SoftBox sx={{ overflow: "visible", backgroundColor: 'transparent', height: '100%' }}>
                <Grid container>
                    <Grid item xxs={12}>
                        <SoftBox p={1}>
                            <Tabs value={tabValue} onChange={handleTabClick} aria-label="DeviceView Tabs"
                                allowScrollButtonsMobile
                                variant="scrollable"
                                scrollButtons
                                sx={{
                                    [`& .${tabsClasses.scrollButtons}`]: {
                                        '&.Mui-disabled': { opacity: 0 },
                                    },
                                    '& .MuiTabs-indicator': {
                                        top: '3px',
                                        left: tabValue == 0 ? '1px !important' : '0px',
                                        height: 'calc(100% - 6px)'
                                    }
                                }}
                            >
                                {tabGeneration()}
                            </Tabs>
                        </SoftBox>
                    </Grid>
                </Grid>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Overview")}>
                    {deviceLayout == 'mobile' ? (
                        <Box pb={1}>
                            <DeviceOverviewTab user={user} device={device_data} />
                        </Box>
                    ) : deviceViewWrapper(
                        <DeviceOverviewTab user={user} device={device_data} />,
                        liveviewInfo,
                        deviceLayout,
                        true
                    )}
                </CustomTabPanel>
                {deviceLayout == 'mobile' && (
                    <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Liveview")}>
                        <Box pb={1}>
                            {liveviewInfo}
                        </Box>
                    </CustomTabPanel>
                )}
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Generate Report")}>
                    <GenerateReportTab {...{ uuid, deviceLayout }} />
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Statistics")}>
                    <Box pb={1}>
                        <StatisticsTab {...{ device: device_data, deviceLayout }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Events")}>
                    <Box pb={1}>
                        <EventsTab {...{ uuid, deviceLayout }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Campaigns")}>
                    <Box pb={1}>
                        <AllCampaignsView {...{ map, user, initialTags, isEmbedded: true }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "History")}>
                    <Box pb={1}>
                        <DeviceInspectionHistoryTab {...{ user, device: device_data, deviceLayout, selectedOrg, userPermissions }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Camera Feed")}>
                    <Box pb={1}>
                        <CameraFeedTab {...{ device: device_data, deviceLayout, directionTrigger }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(userPermissions, device_data, group_data, deviceLayout, "Settings")}>
                    {deviceLayout == 'mobile' ? (
                        <Box pb={1}>
                            <DeviceSettingsTab {...{ uuid, device: device_data, tags: tags.data, raw_device: device }} />
                        </Box>
                    ) : deviceViewWrapper(
                        <DeviceSettingsTab {...{ uuid, device: device_data, tags: tags.data, raw_device: device }} />,
                        liveviewInfo,
                        deviceLayout,
                        true
                    )}
                </CustomTabPanel>
            </SoftBox>
        ), controller, true);
    }
}

DeviceView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DeviceView;
